/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import Model, { Providers } from '@tripian/model';
import classes from './KiwiHotelFilterModal.scss';

export interface KiwiHotelFilters {
  locations: string[];
  specialOffers: boolean;
  ratings: number[];
  settings: string[];
  styles: string[];
  interests: string[];
  brands: string[];
}

export const EMPTY_FILTERS: KiwiHotelFilters = {
  locations: [],
  specialOffers: false,
  ratings: [],
  settings: [],
  styles: [],
  interests: [],
  brands: [],
};

interface IKiwiHotelFilterModal {
  hotels: Providers.Kiwi.KiwiSearchHotel[];
  filters: KiwiHotelFilters;
  onApply: (filters: KiwiHotelFilters) => void;
  onClose: () => void;
  t: (value: Model.TranslationKey) => string;
}

interface FilterOption {
  label: string;
  count: number;
}

/** Aggregate distinct values + counts from an array field across all hotels */
const aggregateArray = (hotels: Providers.Kiwi.KiwiSearchHotel[], field: 'settings' | 'styles' | 'interests'): FilterOption[] => {
  const map = new Map<string, number>();
  hotels.forEach((h) => {
    (h[field] || []).forEach((v: string) => {
      map.set(v, (map.get(v) || 0) + 1);
    });
  });
  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/** Aggregate distinct values + counts from a string field */
const aggregateField = (hotels: Providers.Kiwi.KiwiSearchHotel[], getter: (h: Providers.Kiwi.KiwiSearchHotel) => string): FilterOption[] => {
  const map = new Map<string, number>();
  hotels.forEach((h) => {
    const val = getter(h);
    if (val) map.set(val, (map.get(val) || 0) + 1);
  });
  return Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/** Aggregate star ratings */
const aggregateRatings = (hotels: Providers.Kiwi.KiwiSearchHotel[]): { stars: number; count: number }[] => {
  const map = new Map<number, number>();
  hotels.forEach((h) => {
    const s = Math.round(parseFloat(h.starRating) || 0);
    if (s > 0) map.set(s, (map.get(s) || 0) + 1);
  });
  return Array.from(map.entries())
    .map(([stars, count]) => ({ stars, count }))
    .sort((a, b) => b.stars - a.stars);
};

/** Count how many hotels pass current filters */
const countFiltered = (hotels: Providers.Kiwi.KiwiSearchHotel[], f: KiwiHotelFilters): number =>
  hotels.filter((h) => {
    if (f.locations.length > 0 && !f.locations.includes(h.country)) return false;
    if (f.specialOffers && h.hasSpecialOffer !== 'true') return false;
    if (f.ratings.length > 0) {
      const s = Math.round(parseFloat(h.starRating) || 0);
      if (!f.ratings.includes(s)) return false;
    }
    if (f.settings.length > 0 && !f.settings.some((v) => (h.settings || []).includes(v))) return false;
    if (f.styles.length > 0 && !f.styles.some((v) => (h.styles || []).includes(v))) return false;
    if (f.interests.length > 0 && !f.interests.some((v) => (h.interests || []).includes(v))) return false;
    if (f.brands.length > 0 && !f.brands.includes(h.brand)) return false;
    return true;
  }).length;

const toggleValue = <T extends string | number>(arr: T[], val: T): T[] => (arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val]);

/* ── Sub-components ── */

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className={classes.section}>
    <div className={classes.sectionTitle}>{title}</div>
    <div className={classes.sectionContent}>{children}</div>
  </div>
);

const OptionRow: React.FC<{ label: string | React.ReactNode; count: number; selected: boolean; onToggle: () => void }> = ({ label, count, selected, onToggle }) => (
  <div className={classes.optionRow} onClick={onToggle} role="button" tabIndex={0} onKeyDown={() => {}}>
    <span className={`${classes.radio} ${selected ? classes.radioActive : ''}`} />
    <span className={classes.optionLabel}>{label}</span>
    <span className={classes.optionCount}>{count}</span>
  </div>
);

const renderStars = (n: number): React.ReactNode => (
  <span className={classes.stars}>
    {Array.from({ length: n }, (_, i) => (
      <svg key={i} className={classes.star} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
      </svg>
    ))}
  </span>
);

/* ── Main component ── */

const KiwiHotelFilterModal: React.FC<IKiwiHotelFilterModal> = ({ hotels, filters, onApply, onClose, t }) => {
  const [local, setLocal] = useState<KiwiHotelFilters>({ ...filters });

  /* Aggregated options */
  const locationOpts = useMemo(() => aggregateField(hotels, (h) => h.country), [hotels]);
  const specialOfferCount = useMemo(() => hotels.filter((h) => h.hasSpecialOffer === 'true').length, [hotels]);
  const ratingOpts = useMemo(() => aggregateRatings(hotels), [hotels]);
  const settingOpts = useMemo(() => aggregateArray(hotels, 'settings'), [hotels]);
  const styleOpts = useMemo(() => aggregateArray(hotels, 'styles'), [hotels]);
  const interestOpts = useMemo(() => aggregateArray(hotels, 'interests'), [hotels]);
  const brandOpts = useMemo(() => aggregateField(hotels, (h) => h.brand), [hotels]);

  const filteredCount = useMemo(() => countFiltered(hotels, local), [hotels, local]);

  /* Collect all selected tags for the pill bar */
  const tags: { label: string; remove: () => void }[] = [];
  local.locations.forEach((v) => tags.push({ label: v, remove: () => setLocal((p) => ({ ...p, locations: p.locations.filter((x) => x !== v) })) }));
  if (local.specialOffers) tags.push({ label: t('kiwi.common.specialOffers'), remove: () => setLocal((p) => ({ ...p, specialOffers: false })) });
  local.ratings.forEach((v) => tags.push({ label: `${'★'.repeat(v)}`, remove: () => setLocal((p) => ({ ...p, ratings: p.ratings.filter((x) => x !== v) })) }));
  local.settings.forEach((v) => tags.push({ label: v, remove: () => setLocal((p) => ({ ...p, settings: p.settings.filter((x) => x !== v) })) }));
  local.styles.forEach((v) => tags.push({ label: v, remove: () => setLocal((p) => ({ ...p, styles: p.styles.filter((x) => x !== v) })) }));
  local.interests.forEach((v) => tags.push({ label: v, remove: () => setLocal((p) => ({ ...p, interests: p.interests.filter((x) => x !== v) })) }));
  local.brands.forEach((v) => tags.push({ label: v, remove: () => setLocal((p) => ({ ...p, brands: p.brands.filter((x) => x !== v) })) }));

  const clearAll = () => setLocal({ ...EMPTY_FILTERS });

  return ReactDOM.createPortal(
    <div className={classes.overlay} onClick={onClose} role="presentation">
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={classes.header}>
          <span className={classes.title}>{t('kiwi.filterModal.title')}</span>
          <button type="button" className={classes.closeBtn} onClick={onClose}>
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        {/* Selected tags */}
        {tags.length > 0 && (
          <div className={classes.tagsBar}>
            <div className={classes.tagsList}>
              {tags.map((tag) => (
                <span key={tag.label} className={classes.tag}>
                  {tag.label}
                  <button type="button" className={classes.tagRemove} onClick={tag.remove}>
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
            <button type="button" className={classes.clearAllSmall} onClick={clearAll}>
              {t('kiwi.common.clearAll')}
            </button>
          </div>
        )}

        {/* Scrollable content */}
        <div className={classes.body}>
          {/* Location */}
          {locationOpts.length > 0 && (
            <Section title={t('kiwi.filterModal.location')}>
              {locationOpts.map((o) => (
                <OptionRow key={o.label} label={o.label} count={o.count} selected={local.locations.includes(o.label)} onToggle={() => setLocal((p) => ({ ...p, locations: toggleValue(p.locations, o.label) }))} />
              ))}
            </Section>
          )}

          {/* Special Offers */}
          {specialOfferCount > 0 && (
            <Section title={t('kiwi.common.specialOffers')}>
              <OptionRow label={t('kiwi.common.specialOffers')} count={specialOfferCount} selected={local.specialOffers} onToggle={() => setLocal((p) => ({ ...p, specialOffers: !p.specialOffers }))} />
            </Section>
          )}

          {/* Rating */}
          {ratingOpts.length > 0 && (
            <Section title={t('kiwi.filterModal.rating')}>
              {ratingOpts.map((o) => (
                <OptionRow key={o.stars} label={renderStars(o.stars)} count={o.count} selected={local.ratings.includes(o.stars)} onToggle={() => setLocal((p) => ({ ...p, ratings: toggleValue(p.ratings, o.stars) }))} />
              ))}
            </Section>
          )}

          {/* Setting */}
          {settingOpts.length > 0 && (
            <Section title={t('kiwi.common.setting')}>
              {settingOpts.map((o) => (
                <OptionRow key={o.label} label={o.label} count={o.count} selected={local.settings.includes(o.label)} onToggle={() => setLocal((p) => ({ ...p, settings: toggleValue(p.settings, o.label) }))} />
              ))}
            </Section>
          )}

          {/* Style */}
          {styleOpts.length > 0 && (
            <Section title={t('kiwi.common.style')}>
              {styleOpts.map((o) => (
                <OptionRow key={o.label} label={o.label} count={o.count} selected={local.styles.includes(o.label)} onToggle={() => setLocal((p) => ({ ...p, styles: toggleValue(p.styles, o.label) }))} />
              ))}
            </Section>
          )}

          {/* Interests — two columns */}
          {interestOpts.length > 0 && (
            <Section title={t('kiwi.filterModal.interests')}>
              <div className={classes.twoCol}>
                {interestOpts.map((o) => (
                  <OptionRow key={o.label} label={o.label} count={o.count} selected={local.interests.includes(o.label)} onToggle={() => setLocal((p) => ({ ...p, interests: toggleValue(p.interests, o.label) }))} />
                ))}
              </div>
            </Section>
          )}

          {/* Brand */}
          {brandOpts.length > 0 && (
            <Section title={t('kiwi.filterModal.brand')}>
              {brandOpts.map((o) => (
                <OptionRow key={o.label} label={o.label} count={o.count} selected={local.brands.includes(o.label)} onToggle={() => setLocal((p) => ({ ...p, brands: toggleValue(p.brands, o.label) }))} />
              ))}
            </Section>
          )}
        </div>

        {/* Footer */}
        <div className={classes.footer}>
          <button type="button" className={classes.clearBtn} onClick={clearAll}>
            {t('kiwi.common.clearAll')}
          </button>
          <button type="button" className={classes.applyBtn} onClick={() => onApply(local)}>
            {filteredCount} {t('kiwi.filterModal.showHotels')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default KiwiHotelFilterModal;
